/* « Démarrer » step 3 · « Vos cadeaux » — the walkable set of record (parcours-marchand-v2, guide/3; merchant-app review
   MA-5, journey § 3). One card per gift, prefilled from the sector model (step 1): what the customer wins, how long it
   is valid, a one-line condition, what it costs you (private). « Garder ces N cadeaux » saves them into the library and
   onto the guide's roue (step 4 sets the chances): a gift taken off here leaves that roue (it stays in Vos cadeaux,
   one tap from coming back on step 4); a gift added joins it. The library is Cadeaux's: API seeds + the merchant's own.
   Neither the name nor the condition speaks of a review (D-84: the gift never depends on what the customer answers;
   lib's speaksOfReview, the API's own list). */
import { useState } from 'react'
import { Button, Field, Input, Select, Skeleton, useToast } from '@/ui'
import { useGiftSummary, type Location } from '@/lib/api'
import { MODELS } from '@/lib/models'
import { secteurLabel, useSecteur } from '@/lib/scope'
import { GUIDE_STEPS } from '@/lib/vocab'
import { evenShares } from '@/routes/cadeaux/RoueCourte'
import { toastWhenSaved, writeMark } from '@/lib/writes'
import { ROUE_MAX_TYPES, composeRoueFromTypes, getActiveRoueId, getRoue, getRoues, guessIcon, mergeTypes, newTypeId,
  setActiveRoueId, setRoues, speaksOfReview, toReward, upsertType, type GiftType, type Roue } from '@/lib/wheel'

const VALIDITY = [30, 60, 90, 180, 365]
const validityLabel = (d: number) => d === 180 ? '6 mois' : d === 365 ? '1 an' : `${d} jours`
/* a name or a condition that speaks of a review ties the gift to it — never (D-84) */
const NAME_TIED = 'Le nom d’un cadeau ne parle jamais d’avis.'
const COND_TIED = 'Une condition ne parle jamais d’avis.'
const eur = (n: number) => n ? (Number.isInteger(n) ? String(n) : n.toFixed(2)).replace('.', ',') : ''
const SPELLED = ['', 'Un', 'Deux', 'Trois', 'Quatre', 'Cinq', 'Six', 'Sept', 'Huit', 'Neuf', 'Dix', 'Onze', 'Douze']
const parseEur = (v: string) => parseFloat(v.replace(',', '.').replace(/[^\d.]/g, '')) || 0

type Card = { id: string | null; name: string; validityDays: number; condition: string; cost: string }
/* a new card, as the walkable set adds it */
const fresh = (): Card => ({ id: null, name: 'Un nouveau cadeau', validityDays: 60, condition: '', cost: '2' })

/** The roue the guide sets up — the active one, else the first (the sector model's on day one). */
export const guideRoue = (): Roue | undefined => getRoues().length ? getRoue(getActiveRoueId()) : undefined

const cardOf = (t: GiftType): Card => ({ id: t.id, name: t.name, validityDays: t.validityDays || 60, condition: t.condition ?? '', cost: eur(t.cost) })
/** The guide roue's gifts (a gift missing from the library keeps its card, from the roue's snapshot), else the library. */
function cardsNow(lib: GiftType[]): Card[] {
  const roue = guideRoue()
  const onRoue = (roue?.rewards ?? []).map(x => { const t = lib.find(y => y.id === x.rewardId); return t ? cardOf(t) : { id: x.rewardId, name: x.name, validityDays: 60, condition: '', cost: eur(x.cost) } })
  const cards = onRoue.length ? onRoue : lib.map(cardOf)
  return cards.length ? cards.slice(0, ROUE_MAX_TYPES) : [fresh()]
}

type Props = { loc: Location; onBack: () => void; onNext: () => void }
export default function GuideCadeaux(props: Props) {
  const { data } = useGiftSummary()
  if (!data) return <div style={{ display: 'grid', gap: 10, maxWidth: 820 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  return <Cartes {...props} lib={mergeTypes(data.types)} />
}

function Cartes({ loc, onBack, onNext, lib }: Props & { lib: GiftType[] }) {
  const toast = useToast()
  const secteur = useSecteur()
  const [first] = useState<Card[]>(() => cardsNow(lib))
  const [cards, setCards] = useState<Card[]>(first)
  const set = (i: number, patch: Partial<Card>) => setCards(cards.map((c, k) => k === i ? { ...c, ...patch } : c))
  const n = cards.length
  const keep = n === 3 ? 'Garder ces trois' : n > 1 ? `Garder ces ${n} cadeaux` : 'Garder ce cadeau'

  const garder = () => {
    if (cards.some(c => !c.name.trim())) { toast({ message: 'Donnez un nom à chaque cadeau, ou retirez-le.' }); return }
    if (cards.some(c => speaksOfReview(c.name))) { toast({ message: NAME_TIED }); return }
    if (cards.some(c => speaksOfReview(c.condition))) { toast({ message: COND_TIED }); return }
    /* the library: each card updates its gift, a new card makes one (2b-2: the « ✓ » once the server has them) */
    const mark = writeMark()
    const kept: GiftType[] = cards.map(c => {
      const prev = c.id ? lib.find(t => t.id === c.id) : undefined
      const t: GiftType = prev
        ? { ...prev, name: c.name.trim(), internal: prev.internal || c.name.trim(), cost: parseEur(c.cost), validityDays: c.validityDays, condition: c.condition.trim() }
        : { id: c.id ?? `${newTypeId()}-${Math.random().toString(36).slice(2, 5)}`, name: c.name.trim(), internal: c.name.trim(), cost: parseEur(c.cost), validityDays: c.validityDays, cap: null, condition: c.condition.trim(), status: 'ok', stock: null, icon: guessIcon(c.name), category: 'general', sameDay: false, links: '' }
      upsertType(t)
      return t
    })
    /* the guide's roue follows: removed gifts leave it, new ones join it, names and costs refresh. The same gifts keep
       their chances; a gift added or taken off means equal shares again (step 4 sets them) */
    const roue = guideRoue()
    if (!roue) composeRoueFromTypes(kept, `Roue de ${loc.name}`, [loc.id])
    else {
      const rewards = kept.map(t => { const x = roue.rewards.find(r => r.rewardId === t.id); return x ? { ...x, name: t.name, cost: t.cost } : toReward(t, 0) })
      const same = rewards.length === roue.rewards.length && rewards.every(x => x.weight > 0)
      const next = same ? { ...roue, rewards } : evenShares({ ...roue, rewards })
      /* a roue a campaign uses: a change shows « modifications à publier » until that campaign republishes (D-29) */
      const changed = JSON.stringify(next.rewards) !== JSON.stringify(roue.rewards)
      setRoues(getRoues().map(w => w.id === roue.id ? { ...next, pending: !!roue.pending || (changed && next.usedBy.length > 0) } : w)); setActiveRoueId(roue.id)
    }
    toastWhenSaved(mark, toast, `✓ ${n} cadeau${n > 1 ? 'x' : ''} enregistré${n > 1 ? 's' : ''}.`)
    onNext()
  }

  const p = first.length
  const prepared = secteur && MODELS[secteur] ? `${SPELLED[p] ?? p} cadeau${p > 1 ? 'x' : ''} préparé${p > 1 ? 's' : ''} pour « ${secteurLabel(secteur).toLowerCase()} ». ` : ''
  return (
    <div data-testid="guide-cadeaux" style={{ maxWidth: 820 }}>
      <p className="t-eyebrow" style={{ margin: '0 0 4px' }}>Démarrer · étape 3 sur 6</p>
      <h2 className="t-page" style={{ margin: '0 0 4px' }}>{GUIDE_STEPS[2].title}</h2>
      <p className="t-body" style={{ margin: '0 0 20px', maxWidth: 680 }}>Qu’est-ce que vos clients peuvent gagner&nbsp;? {prepared}Changez ce que vous voulez.</p>
      <div style={{ display: 'grid', gap: 16 }}>
        {cards.map((c, i) => (
          <section key={i} className="card" data-testid={`cadeau-${i + 1}`} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p className="f-label" style={{ margin: 0 }}>Cadeau {i + 1}</p>
              {n > 1 && <Button size="sm" variant="quiet" onClick={() => setCards(cards.filter((_, k) => k !== i))}>Retirer</Button>}
            </div>
            <Field label="Ce que gagne le client" error={speaksOfReview(c.name) ? NAME_TIED : undefined}>{id => <Input id={id} value={c.name} onChange={e => set(i, { name: e.target.value })} placeholder="Un café offert" />}</Field>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
              <Field label="Valable">{id => (
                <Select id={id} value={String(c.validityDays)} onChange={e => set(i, { validityDays: Number(e.target.value) })}>
                  {[...new Set([...VALIDITY, c.validityDays])].sort((a, b) => a - b).map(d => <option key={d} value={d}>{validityLabel(d)}</option>)}
                </Select>
              )}</Field>
              <Field label="Condition (une ligne)" error={speaksOfReview(c.condition) ? COND_TIED : undefined}>{id => <Input id={id} value={c.condition} maxLength={140} onChange={e => set(i, { condition: e.target.value })} placeholder="Facultative, par ex. « Sur rendez-vous. »" />}</Field>
            </div>
            <div style={{ maxWidth: 240 }}>
              <Field label="Ce qu’il vous coûte" help="Pour vous seul : le client ne le voit jamais.">{id => <Input id={id} value={c.cost} inputMode="decimal" onChange={e => set(i, { cost: e.target.value })} placeholder="2,50" />}</Field>
            </div>
          </section>
        ))}
        {n < ROUE_MAX_TYPES
          ? <button type="button" data-testid="ajouter-cadeau" onClick={() => setCards([...cards, fresh()])}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 16, border: '2px dashed var(--bmr-border)', borderRadius: 14, background: 'transparent', cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left' }}>
              <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>+</span><span><b>Ajouter un cadeau</b> <span className="f-help">· douze au plus sur une roue</span></span></button>
          : <p className="f-help" style={{ margin: 0 }}>Douze cadeaux au plus sur une roue.</p>}
        <p className="f-help" style={{ margin: 0 }}>La condition ne peut pas parler d’avis&nbsp;: le cadeau ne dépend jamais de ce que le client répond.</p>
      </div>
      <div className="actions" style={{ marginTop: 20 }}>
        <Button variant="quiet" onClick={onBack}>‹ Retour</Button>
        <Button variant="primary" onClick={garder} data-testid="garder-cadeaux">{keep}&nbsp;→</Button>
      </div>
    </div>
  )
}
